import {
  Component,
  Host,
  h,
  Prop,
  State,
  Element,
  Event,
  EventEmitter,
  getAssetPath
} from "@stencil/core";

import { MercuryBundles, getIconPath } from "@genexus/mercury";

import { Locale } from "../../../common/locale";
import { config } from "../../../common/config";
import { ComboBoxModel } from "@genexus/chameleon-controls-library";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/button",
  "components/combo-box",
  "components/icon",
  "components/tabular-grid",
  "utils/elevation",
  "utils/form",
  "utils/layout",
  "utils/spacing"
];

const ADD_ICON = getIconPath({
  category: "system",
  name: "add",
  colorType: "interactive"
});
const FILTER_ICON = getIconPath({
  category: "window-tools",
  name: "filter",
  colorType: "on-surface"
});
const MORE_ICON = getIconPath({
  category: "system",
  name: "more-horizontal",
  colorType: "primary"
});

const SUGGEST_OPTIONS = {
  alreadyProcessed: true,
  autoExpand: false,
  hideMatchesAndShowNonMatches: false,
  highlightMatchedItems: false,
  matchCase: false,
  regularExpression: false,
  strict: false,
  renderActiveItemIconOnExpand: true
};

@Component({
  tag: "gx-ide-bpm-app-declaration",
  styleUrl: "bpm-app-declaration.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/bpm-app-declaration"]
})
export class GxIdeBpmAppDeclaration {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;
  #animateEmptyState = true;
  #shortcutsSrc = getAssetPath(
    `./gx-ide-assets/bpm-app-declaration/shortcuts.json`
  );

  #ACCESS_CAPTION_MAP: Record<DataMappingAccess, string> = {
    // To be defined on componentWillLoad
    in: undefined,
    out: undefined,
    inout: undefined
  };

  @Element() el: HTMLGxIdeBpmAppDeclarationElement;

  /**
   * The application ComoBoxModel. When the component renders for the first time, it is required
   * to display the application typeIcon. Later, it is required to display the suggested application
   * options with the combo suggest.
   */
  @State() applicationComboBoxModel: ComboBoxModel = [];

  /**
   * Current value of the application declaration. Keep in mind that this property will be used by
   * the host to initialize the component, meaning it will assign the current declaration value.
   */
  @Prop({ mutable: true }) application: ApplicationDeclarationData;

  /**
   * Callback invoked when user cancels application declaration
   */
  @Prop() readonly cancelCallback: CancelCallback;

  /**
   * Callback invoked when user confirms application declaration
   */
  @Prop() readonly confirmCallback: ConfirmCallback;

  /**
   * Callback invoked when user select one of the options provided by the suggest
   */
  @Prop() readonly getDataMappingCallback: GetDataMappingCallback;

  /**
   * Callback invoked to get access to selection application dialog
   */
  @Prop() readonly selectApplicationCallback: SelectApplicationCallback;

  /**
   * Callback invoked when user press mapping auto-def button
   */
  @Prop() readonly selectMappingCallback: SelectMappingCallback;

  /**
   * Callback invoked to get options for application input suggest
   */
  @Prop() readonly suggestApplicationsCallback: SuggestApplicationsCallback;

  /**
   * @description Gets fired when the component has rendered for the first time.
   */
  @Event() componentDidRenderFirstTime: EventEmitter<string>;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.#setApplicationData(this.application.application);
    if (this.application.mappings.length === 0) {
      this.#animateEmptyState = false;
    }
    this.#setAccessCaptions();
  }

  #setAccessCaptions = () => {
    this.#ACCESS_CAPTION_MAP.in = this.#componentLocale.dataMappings.access.in;
    this.#ACCESS_CAPTION_MAP.out =
      this.#componentLocale.dataMappings.access.out;
    this.#ACCESS_CAPTION_MAP.inout =
      this.#componentLocale.dataMappings.access.inout;
  };

  #applicationInputChangedHandler = async (
    event: CustomEvent<string> | InputEvent
  ) => {
    const selectedApplicationName = event.detail as string;
    this.application.mappings = await this.getDataMappingCallback(
      selectedApplicationName
    );
  };

  #cancelCallbackHandler = () => {
    this.cancelCallback();
  };

  #clearApplicationHandler = () => {
    this.application = {
      application: {
        name: "",
        typeIcon: ""
      },
      mappings: []
    };
  };

  #confirmCallbackHandler = () => {
    this.confirmCallback(this.application);
  };

  #mapRelevantDataOptionsToComboBoxModel = (
    relevantDataOptions: string[]
  ): ComboBoxModel => {
    return relevantDataOptions.map(optionName => {
      return {
        caption: optionName,
        value: optionName
      };
    });
  };

  #mapSuggestedApplicationsToComboBoxModel = (
    suggestedApplicationsNames: string[]
  ): ComboBoxModel => {
    return suggestedApplicationsNames.map(applicationName => {
      return {
        caption: applicationName,
        value: applicationName
      };
    });
  };

  #relevantDataInputHandler =
    (mappingIndex: number) =>
    async (event: CustomEvent<string> | InputEvent) => {
      const inputValue = event.detail as string;
      this.application.mappings[mappingIndex].relevantData = inputValue;
    };

  #selectApplicationCallbackHandler = async () => {
    const applicationDeclarationData = await this.selectApplicationCallback();
    if (applicationDeclarationData) {
      this.#setApplicationData(applicationDeclarationData.application);
      this.application.mappings = applicationDeclarationData.mappings;
    }
  };

  #selectMappingCallbackHandler =
    (parameter: string, mappingIndex: number) => async () => {
      const returnedRelevantData = await this.selectMappingCallback(parameter);
      if (returnedRelevantData) {
        const currentRelevantDataOptions =
          this.application.mappings[mappingIndex].relevantDataOptions;

        const indexOfReturnedRelevantData =
          currentRelevantDataOptions.indexOf(returnedRelevantData);

        if (indexOfReturnedRelevantData === -1) {
          // returnedRelevantData is a new option. Add to combo-box.
          currentRelevantDataOptions.push(returnedRelevantData);
        }
        // Set as selected
        this.application.mappings[mappingIndex].relevantData =
          returnedRelevantData;

        this.#updateApplicationData();
      }
    };

  #setApplicationData = (applicationData: ApplicationData) => {
    if (applicationData) {
      const applicationName = applicationData.name;
      this.application.application.name = applicationName;
      this.applicationComboBoxModel = [
        {
          caption: applicationName,
          value: applicationName,
          startImgSrc: applicationData.typeIcon
        }
      ];
    }
  };

  #suggestInputHandler = async (event: CustomEvent<string> | InputEvent) => {
    const inputValue = event.detail as string;
    this.application.application.name = inputValue;
    const suggestedApplicationNames: string[] =
      await this.suggestApplicationsCallback(inputValue);

    this.applicationComboBoxModel =
      this.#mapSuggestedApplicationsToComboBoxModel(suggestedApplicationNames);
  };

  #updateApplicationData = () => {
    this.application = { ...this.application };
  };

  // Renders

  #renderDataMappingsGrid = (): Element => {
    const applicationDataToDisplay = this.application.mappings.length;
    if (applicationDataToDisplay) {
      // Set as animated for the next time.
      this.#animateEmptyState = true;
    }
    return (
      <div class="field-group data-mapping-grid-container">
        <div class="field field-block">
          <label class="spacing-body-inline-start label">
            {this.#componentLocale.dataMappings.title}
          </label>
          <ch-tabular-grid
            class={{
              "tabular-grid": true,
              "tabular-grid-application-declaration": true,
              "scrollable": true,
              "empty-result": !applicationDataToDisplay
            }}
            part="ch-grid-data-mappings"
          >
            <ch-tabular-grid-columnset class="tabular-grid-column-set">
              <ch-tabular-grid-column
                class="tabular-grid-column"
                columnName={
                  this.application.application?.typeIcon ===
                  "objects/transaction"
                    ? this.#componentLocale.dataMappings.firstColName
                    : this.#componentLocale.dataMappings.firstColNameParameter
                }
                settingable={false}
                sortable={false}
                size={config.tabularGrid.colSize.common}
              ></ch-tabular-grid-column>

              <ch-tabular-grid-column
                class="tabular-grid-column"
                columnName={this.#componentLocale.dataMappings.secondColName}
                settingable={false}
                sortable={false}
                size={config.tabularGrid.colSize.common}
              ></ch-tabular-grid-column>
              <ch-tabular-grid-column
                class="tabular-grid-column"
                settingable={false}
                sortable={false}
                size={config.tabularGrid.colSize.auto}
              ></ch-tabular-grid-column>
            </ch-tabular-grid-columnset>

            {this.application.mappings.length ? (
              <ch-tabular-grid-rowset class="tabular-grid-rowset">
                {this.application.mappings.map(
                  (obj: DataMappingData, index) => (
                    <ch-tabular-grid-row
                      class="tabular-grid-row"
                      rowId={`${index}-${obj.parameter}`}
                      key={`${index}-${obj.parameter}-grid-row`}
                    >
                      <ch-tabular-grid-cell class="tabular-grid-cell">
                        <span class="pill-tag elevation-3">
                          {
                            this.#ACCESS_CAPTION_MAP[
                              obj.access as DataMappingAccess
                            ]
                          }
                        </span>
                        {obj.parameter}
                      </ch-tabular-grid-cell>
                      <ch-tabular-grid-cell class="tabular-grid-cell">
                        <ch-combo-box-render
                          class="combo-box"
                          model={this.#mapRelevantDataOptionsToComboBoxModel(
                            obj.relevantDataOptions
                          )}
                          value={obj.relevantData}
                          key={`${index}-${obj.parameter}-combo-box`}
                          onInput={this.#relevantDataInputHandler(index)}
                        ></ch-combo-box-render>
                      </ch-tabular-grid-cell>
                      <ch-tabular-grid-cell class="tabular-grid-cell">
                        <button
                          data-index={index}
                          onClick={this.#selectMappingCallbackHandler(
                            obj.parameter,
                            index
                          )}
                          class="button-tertiary button-icon-only"
                          aria-label={
                            this.#componentLocale.buttons.selectMapping
                          }
                          title={this.#componentLocale.buttons.selectMapping}
                        >
                          <ch-image class="icon-m" src={ADD_ICON}></ch-image>
                        </button>
                      </ch-tabular-grid-cell>
                    </ch-tabular-grid-row>
                  )
                )}
              </ch-tabular-grid-rowset>
            ) : (
              <ch-tabular-grid-rowset class="tabular-grid-rowset">
                <ch-tabular-grid-rowset-empty>
                  <gx-ide-empty-state
                    isAnimated={this.#animateEmptyState}
                    stateIconSrc={FILTER_ICON}
                    stateTitle={this.#componentLocale.emptyState.title}
                    stateDescription={
                      this.#componentLocale.emptyState.description
                    }
                  ></gx-ide-empty-state>
                </ch-tabular-grid-rowset-empty>
              </ch-tabular-grid-rowset>
            )}
          </ch-tabular-grid>
        </div>
      </div>
    );
  };

  render() {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <section class="section">
          <header slot="header" class="header spacing-body">
            <ch-combo-box-render
              class="combo-box"
              value={this.application.application.name}
              model={this.applicationComboBoxModel}
              suggestDebounce={config.inputDebounce}
              suggest
              suggestOptions={SUGGEST_OPTIONS}
              onInput={this.#suggestInputHandler}
              onChange={this.#applicationInputChangedHandler}
            ></ch-combo-box-render>

            <div class="buttons-spacer">
              <button
                onClick={this.#selectApplicationCallbackHandler}
                class="button-tertiary button-icon-only"
                aria-label={this.#componentLocale.buttons.selectApplication}
                title={this.#componentLocale.buttons.selectApplication}
              >
                <ch-image class="icon-m" src={MORE_ICON}></ch-image>
              </button>
              <button
                onClick={this.#clearApplicationHandler}
                class="button-secondary"
              >
                Clear
              </button>
            </div>
          </header>

          {this.#renderDataMappingsGrid()}

          <footer class="dialog-footer spacing-body-block-end spacing-body-inline">
            <div class="buttons-spacer">
              <button
                class="button-secondary"
                onClick={this.#cancelCallbackHandler}
              >
                {this.#componentLocale.buttons.cancelButton}
              </button>
              <button
                class="button-primary"
                onClick={this.#confirmCallbackHandler}
              >
                {this.#componentLocale.buttons.confirmButton}
              </button>
            </div>
          </footer>
        </section>
        <ch-shortcuts src={this.#shortcutsSrc}></ch-shortcuts>
      </Host>
    );
  }
}

export type ApplicationDeclarationData = {
  application?: ApplicationData;
  mappings: DataMappingData[];
};
export type ApplicationData = {
  name: string;
  typeIcon: string;
};

type DataMappingAccess = "in" | "out" | "inout";

export type DataMappingData = {
  parameter: string;
  access: DataMappingAccess;
  relevantDataOptions: string[];
  relevantData?: string;
};

export type SuggestApplicationsCallback = (
  prefix: string
) => Promise<string[] | undefined>;

export type SelectApplicationCallback = () => Promise<
  ApplicationDeclarationData | undefined
>;

export type SelectMappingCallback = (parameter: string) => Promise<string>;

export type ConfirmCallback = (
  data: ApplicationDeclarationData
) => Promise<void>;

export type CancelCallback = () => Promise<void>;

export type GetDataMappingCallback = (
  applicationName: string
) => Promise<DataMappingData[]>;
